import { useQuery } from "@tanstack/react-query";
import { useApi } from "@utils/useApi";
import {
  ISearchOrderRequestFilter,
  OrdersClient,
  SearchOrderRequestFilter,
  SearchOrdersRequest,
} from "@lib/ShiOneClient";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import ms from "ms";
import { OrderViewType } from "@widgets/ecommerce/types/OrderViewType";
import { OrderRow } from "@features/orders-features/types/types";
import { getOrdersViewType } from "@features/orders-features/functions/getOrdersViewType";
import type { FilterBarProps } from "shared-ui/src/components/filter-bar/FilterBar";
import { OrdersQueryKeys } from "@features/orders-features/hooks/OrdersQueryKeys";
import mapToOrderRows from "@features/orders-features/functions/mapToOrderRows";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import { applyInvoiceStatusFilter } from "@features/orders-features/functions/applyInvoiceStatusFilter";

export interface UseSearchOrdersOptions {
  enabled?: boolean;
  view?: OrderViewType;
  crmAccountId?: string;
  employeeId?: number;
  query?: string;
  filter?: FilterBarProps;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  pageSize?: number;
}

export interface SearchOrdersPagination {
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface SearchOrdersResult {
  items: OrderRow[];
  pagination: SearchOrdersPagination;
}

export default function useSearchOrders(options: UseSearchOrdersOptions = {}) {
  const api = useApi(OrdersClient);
  const { hasPermission } = useClaims();

  const searchMyOrders = hasPermission(ShiOnePermission.orderSearchMy);
  const searchOrgUnitOrders = hasPermission(
    ShiOnePermission.orderSearchOrgUnit,
  );

  const {
    view,
    crmAccountId,
    employeeId,
    filter,
    sortBy,
    sortDirection,
    page = 1,
    pageSize = 100,
  } = options;

  const searchString = options.query?.trim();

  const canFetchMy = searchMyOrders && !!employeeId;

  const canSearch =
    (view === OrderViewType.My && canFetchMy) ||
    (view === OrderViewType.Storefront && searchOrgUnitOrders) ||
    (canFetchMy && searchOrgUnitOrders);
  const enabled = options.enabled ?? canSearch;

  const fetchOrders = async (): Promise<SearchOrdersResult> => {
    const mapFilter = (
      filterMap?: FilterBarProps["include"] | FilterBarProps["exclude"],
    ): SearchOrderRequestFilter | undefined => {
      if (!filterMap) return undefined;

      const keys: Array<keyof ISearchOrderRequestFilter> = [
        "orderedBy",
        "orderDateRange",
        "orderNumber",
        "purchaseOrderNumber",
        "shipTo",
        "status",
        "orgUnitIds",
        "biOrderIds",
        "webOrderIds",
      ];

      const mapped: Partial<ISearchOrderRequestFilter> = {};

      keys.forEach((k) => {
        const v = (filterMap as any)[k as string];
        if (v === undefined) {
          return;
        }
        (mapped as any)[k] = k === "orgUnitIds" ? v.map(Number) : v;
      });

      return new SearchOrderRequestFilter(mapped as ISearchOrderRequestFilter);
    };

    const translated = applyInvoiceStatusFilter(
      filter?.include as Record<string, unknown> | undefined,
      filter?.exclude as Record<string, unknown> | undefined,
    );
    const searchFilterInclude = mapFilter(
      translated.include as FilterBarProps["include"],
    );
    const searchFilterExclude = mapFilter(
      translated.exclude as FilterBarProps["exclude"],
    );

    const request = new SearchOrdersRequest({
      ordersViewType: getOrdersViewType(view),
      crmAccountId: crmAccountId,
      page,
      pageSize,
      searchString,
      ...(searchFilterInclude ? { searchFilterInclude } : {}),
      ...(searchFilterExclude ? { searchFilterExclude } : {}),
      sortByColumn: sortBy,
      isDescending: sortDirection === "desc",
    });

    const response = await api.searchOrders(request);
    return mapToOrderRows(response, { page, pageSize });
  };

  return useQuery({
    queryKey: [
      OrdersQueryKeys.Search,
      view,
      crmAccountId,
      employeeId,
      searchString,
      filter,
      sortBy,
      sortDirection,
      page,
      pageSize,
    ],
    queryFn: fetchOrders,
    enabled,
    staleTime: ms("0s"),
    refetchInterval: ms("15m"),
    refetchOnWindowFocus: true,
  });
}
